/**
 * `<ChatErrorBoundary>` — defence-in-depth wrapper around the chat
 * message list + tool-call cards.
 *
 * Issue #936 — before this boundary existed, a `.map()` over a
 * non-array (the canonical case: `block.questions` was sometimes
 * shipped as an object-keyed-by-id by an intermediate SKILL
 * bootstrap state — see `build-game-state.ts:231` for the main-
 * side guard the renderer was missing) propagated all the way to
 * the React root and unmounted the entire Workspace. The user saw
 * a stuck loop where the agent kept retrying a "broken" tool call
 * while the chat looked frozen.
 *
 * The boundary turns that catastrophic mode into:
 *
 *   1. A user-readable fallback card explaining what happened and
 *      the terminal-fallback workaround (`crucible build-game` from
 *      the game dir) — sourced verbatim from the issue body's
 *      recommended copy.
 *   2. A `diagnostics:capture-renderer-issue` IPC firing with the
 *      caught `Error`'s `message + stack + componentStack` so we
 *      get a real repro from production, not a minified `H.map`
 *      stack trace.
 *   3. A "Try again" affordance that resets the boundary state — a
 *      transient render error (e.g. a state slice flipped from
 *      malformed → well-formed via the next stream chunk) recovers
 *      on next render. Persistent crashes loop back to the
 *      fallback.
 *
 * The boundary is a **React class component** because
 * `componentDidCatch` / `getDerivedStateFromError` aren't available
 * on functional components. The fallback card itself is functional —
 * scoped to this file so the boundary's blast-radius is one module.
 */
import React from 'react';

/**
 * Surface-level context carrying the active `gameId` to chat
 * descendants. Consumed by `<SchemaErrorCard>` (and any future card
 * that wants to fire a `diagnostics:capture-renderer-issue` IPC)
 * without having to prop-drill `gameId` through `ChatBubble` and
 * `AskUserQuestionCard`. The boundary is the natural provider
 * because it already owns the gameId for its own capture IPC.
 *
 * Defaulting to `{ gameId: undefined }` keeps consumers under the
 * boundary safe even if a future render path mounts a card without
 * the provider — they'll just skip the diagnostic IPC, matching the
 * boundary's own `!this.props.gameId` skip.
 */
export const ChatSurfaceContext = React.createContext<{ gameId?: string }>({
  gameId: undefined,
});

/** Renderer-facing IPC bridge — duplicated here from `window.crucible`
 *  so the boundary doesn't import the full preload typings (keeps the
 *  boundary tree-shakeable for tests that don't have a `window`
 *  global). The shape matches `CrucibleApi.invoke` exactly. */
interface DiagnosticsBridge {
  invoke(
    channel: 'diagnostics:capture-renderer-issue',
    args: {
      gameId: string;
      kind: 'render-error';
      message: string;
      stack?: string;
      componentStack?: string;
    },
  ): Promise<{ ok: boolean; filePath?: string }>;
}

export interface ChatErrorBoundaryProps {
  /** Children to render. The boundary catches any throw under this
   *  subtree at render / lifecycle time. Event-handler throws and
   *  async errors (Promise rejections, setTimeout callbacks) are NOT
   *  caught — React error boundaries only catch synchronous render-
   *  phase errors. The relevant `.map` crashes for #936 are render-
   *  phase, so this is correctly scoped. */
  children: React.ReactNode;
  /** When present, the captured diagnostic is filed under the game's
   *  `.foundry/diagnostics/` directory. Without it the boundary
   *  still renders the fallback but skips the IPC. The chat surface
   *  always knows its gameId by the time it mounts — `undefined`
   *  here is a defence against a future render path mounting the
   *  boundary before route params resolve. */
  gameId?: string;
  /** Optional injection seam for tests. Production reads from
   *  `window.crucible`. */
  bridge?: DiagnosticsBridge;
  /** Optional override for the fallback UI. Tests pass a stub to
   *  assert the boundary's `state.hasError` transitions without
   *  having to match the full DOM. */
  renderFallback?: (args: {
    error: Error;
    onReset: () => void;
    gameId?: string;
  }) => React.ReactNode;
}

interface ChatErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  /** Captured in `componentDidCatch` so the IPC payload carries the
   *  React component stack alongside the JavaScript stack. Lives in
   *  state (not just a captured local) so the fallback UI can show
   *  the component stack to support if requested. */
  componentStack: string | null;
}

export class ChatErrorBoundary extends React.Component<
  ChatErrorBoundaryProps,
  ChatErrorBoundaryState
> {
  override state: ChatErrorBoundaryState = {
    hasError: false,
    error: null,
    componentStack: null,
  };

  static getDerivedStateFromError(error: Error): Partial<ChatErrorBoundaryState> {
    // Render the fallback on the same render pass that produced the
    // error — without this, the broken subtree would re-render and
    // re-throw. Component-stack capture lives in `componentDidCatch`
    // because `getDerivedStateFromError` doesn't get it.
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const componentStack = errorInfo.componentStack ?? null;
    // Belt-and-braces: state was already partially set in
    // `getDerivedStateFromError`. Re-assign so the component stack
    // makes it onto the same record the fallback reads.
    this.setState({ componentStack });
    const bridge = this.resolveBridge();
    if (!bridge || !this.props.gameId) return;
    // Fire-and-forget. The capture IPC is best-effort by contract;
    // a `.catch` here only exists so an unhandled rejection doesn't
    // surface in the renderer's error log (which would compound the
    // user-facing error we're trying to surface a soft fallback
    // for).
    void bridge
      .invoke('diagnostics:capture-renderer-issue', {
        gameId: this.props.gameId,
        kind: 'render-error',
        message: error.message,
        stack: error.stack,
        componentStack: componentStack ?? undefined,
      })
      .catch(() => {
        // Swallow — log path lives main-side. Nothing useful we can
        // do renderer-side beyond NOT re-throwing.
      });
  }

  /**
   * Reset the boundary's error state. Used by the fallback's "Try
   * again" button. If the underlying issue persists, the next
   * render simply re-throws and we land back on the fallback —
   * idempotent on a stuck state.
   */
  private handleReset = (): void => {
    this.setState({ hasError: false, error: null, componentStack: null });
  };

  /** Resolve the IPC bridge. Tests pass `props.bridge`; production
   *  reads from `window.crucible`. SSR / vitest's node-only env
   *  returns `null` and the capture path is skipped. */
  private resolveBridge(): DiagnosticsBridge | null {
    if (this.props.bridge) return this.props.bridge;
    if (typeof window !== 'undefined') {
      const w = window as unknown as { crucible?: DiagnosticsBridge };
      if (w.crucible) return w.crucible;
    }
    return null;
  }

  override render(): React.ReactNode {
    if (this.state.hasError && this.state.error) {
      const error = this.state.error;
      // The fallback renders OUTSIDE the surface-context provider —
      // it's a terminal state, no descendant cards need the gameId.
      if (this.props.renderFallback) {
        return this.props.renderFallback({
          error,
          onReset: this.handleReset,
          gameId: this.props.gameId,
        });
      }
      return (
        <ChatErrorFallback error={error} onReset={this.handleReset} gameId={this.props.gameId} />
      );
    }
    // Happy path — provide the surface-level gameId so descendants
    // (e.g. `SchemaErrorCard`) can fire their own
    // `diagnostics:capture-renderer-issue` IPCs without prop-drill.
    return (
      <ChatSurfaceContext.Provider value={{ gameId: this.props.gameId }}>
        {this.props.children}
      </ChatSurfaceContext.Provider>
    );
  }
}

/**
 * Default fallback card. Mirrors the visual treatment of
 * `<SchemaErrorCard>` in `pages/Workspace.tsx` (amber-bordered alert)
 * so users recognise it as the same family of recoverable error.
 * Copy is sourced from issue #936's recommended user-facing message.
 */
function ChatErrorFallback({
  error,
  onReset,
  gameId,
}: {
  error: Error;
  onReset: () => void;
  gameId?: string;
}): React.JSX.Element {
  return (
    <div
      role="alert"
      data-testid="chat-error-boundary-fallback"
      className="mx-4 my-3 rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-3"
    >
      <div className="flex items-start gap-2">
        <span aria-hidden="true" className="mt-0.5 text-amber-300/80">
          !
        </span>
        <div className="flex-1">
          <p className="text-xs uppercase tracking-wide text-amber-200/80">
            Chat hit a render error
          </p>
          <p className="mt-1 text-sm text-white/80">
            Foundry caught a runtime error while rendering this chat surface. A diagnostic has been
            saved
            {gameId ? (
              <>
                {' '}
                to{' '}
                <code className="rounded bg-black/30 px-1 py-0.5 text-xs">
                  .foundry/diagnostics/
                </code>{' '}
                in your game directory.
              </>
            ) : (
              <> for support.</>
            )}
          </p>
          <p className="mt-2 text-sm text-white/70">
            <span className="font-medium">Workaround:</span> open a terminal in your game directory
            and run{' '}
            <code className="rounded bg-black/30 px-1 py-0.5 text-xs">crucible build-game</code> to
            continue the interview without the chat UI.
          </p>
          <details className="mt-2 text-xs text-white/40">
            <summary className="cursor-pointer hover:text-white/60">Error details</summary>
            <pre className="mt-1 max-h-24 overflow-auto whitespace-pre-wrap break-words text-[0.6875rem] text-white/50">
              {error.message}
            </pre>
          </details>
          <button
            type="button"
            onClick={onReset}
            data-testid="chat-error-boundary-reset"
            className="mt-3 inline-flex items-center rounded-md border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium text-white/70 transition-colors hover:border-amber-500/40 hover:text-white"
          >
            Try again
          </button>
        </div>
      </div>
    </div>
  );
}
